import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSlideToggle, MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { basicSetup } from '@codemirror/next/basic-setup';
import { EditorView } from '@codemirror/next/view';
import { EditorState } from '@codemirror/next/state';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { MathService } from '../math.service';

@Component({
  selector: 'sfo-math-editor',
  templateUrl: './math-editor.component.html',
  styleUrls: ['./math-editor.component.scss'],
  standalone: false,
})
export class MathEditorComponent {
  @ViewChild('displayToggle') displayToggle: MatSlideToggle;
  @ViewChild('preview', { static: true }) preview: ElementRef;
  @ViewChild('editor') editorRef?: ElementRef;

  results$ = new Subject<{ errors: any[]; node: SafeHtml | null }>();
  lastResult: { errors: any[]; node: SafeHtml | null } | null = null;
  useMathJax$ = new BehaviorSubject(localStorage.getItem('use-math') !== 'true');

  @Input() set numbered(val: boolean) {
    this.isInline$.next(!val);
  }
  @Input() toggleHelp = true;
  @Input() set tex(tex: string) {
    this.texInput$.next(tex);
  }

  constructor(
    public dialogRef: MatDialogRef<MathEditorComponent>,
    private sanitizer: DomSanitizer,
    private mathService: MathService,
    private snackBar: MatSnackBar,
  ) {}
  /*** Input from outside (e.g. the editor node) */
  texInput$ = new BehaviorSubject<string>('');
  view?: EditorView;

  commandInput$ = new Subject<string>();
  previewValidation$ = new Subject<string>();
  isInline$ = new BehaviorSubject<boolean>(true);

  editorHasCursor$ = new BehaviorSubject<boolean>(false);

  equationsList = [
    {
      label: 'The Lorenz Equations',
      content: 'x = {-b \\pm \\sqrt{b^2-4ac} \\over 2a}',
      tooltip: 'Insert the quadratic formula',
    },
    {
      label: 'The Cauchy-Schwarz Inequality',
      content: `\\begin{align}
      \\dot{x} = \\sigma(y-x) \\\\
      \\dot{y} = \\rho x - y - xz \\\\
      \\dot{z} = -\\beta z + xy \\\\
\\end{align}`,
      tooltip: 'Insert an aligned equation',
    },
    {
      label: 'A Cross Product Formula',
      content: `\\mathbf{V}_1 \\times \\mathbf{V}_2 =
    \\begin{vmatrix}
      \\mathbf{i} & \\mathbf{j} & \\mathbf{k} \\\\
      \\frac{\\partial X}{\\partial u} & \\frac{\\partial Y}{\\partial u} & 0 \\\\
      \\frac{\\partial X}{\\partial v} & \\frac{\\partial Y}{\\partial v} & 0 \\\\
    \\end{vmatrix}`,
    },
  ];

  environments = [
    {
      label: 'Align',
      content: '\\begin{align}\\end{align}',
      tooltip: 'Insert an align environment',
    },
    {
      label: 'Gather',
      content: '\\begin{gather}\\end{gather}',
      tooltip: 'Insert a gather environment',
    },
    {
      label: 'Equation',
      content: '\\begin{equation}\\end{equation}',
      tooltip: 'Insert an equation environment',
    },
  ];

  toolbarList = [
    { label: 'frac {a}/{b}', content: '\\frac{a}{b}', tooltip: 'Insert a fraction' },
    { label: 'x²', content: 'x^2', tooltip: 'Insert an exponent' },
    { label: '∑', content: '\\sum\\limits_{i = 1}^n {a_i}', tooltip: 'Insert a summation' },
    { label: 'α', content: '\\alpha', tooltip: 'Insert alpha' },
    { label: 'β', content: '\\beta', tooltip: 'Insert beta' },
    { label: '√', content: '\\sqrt{}', tooltip: 'Insert square root' },
    { label: 'π', content: '\\pi', tooltip: 'Insert pi' },
    { label: 'θ', content: '\\theta', tooltip: 'Insert theta' },
    { label: 'λ', content: '\\lambda', tooltip: 'Insert lambda' },
    { label: 'Ω', content: '\\Omega', tooltip: 'Insert omega' },
  ];

  ngAfterViewInit() {
    // Note: The render() method (called from EditorView.updateListener) handles
    // updating results$ which the template uses to display errors.
    // This subscription is kept for backward compatibility but render() is the primary path.

    this.view = new EditorView({
      state: EditorState.create({
        doc: this.texInput$.value,
        extensions: [
          basicSetup,
          EditorView.updateListener.of(({ state, view }) => {
            const content = state.doc.toString();
            this.texInput$.next(content);
            this.render(content);

            const hasFocus = view.hasFocus;
            const hasSelection = state.selection.ranges.length > 0;
            this.editorHasCursor$.next(hasFocus && hasSelection);
          }),
        ],
      }),
      parent: this.editorRef?.nativeElement,
    });

    // Initial render - updateListener may not fire on view creation
    if (this.texInput$.value) {
      this.render(this.texInput$.value);
    }

    setTimeout(() => {
      this.setFocus();
    }, 250);
  }

  setFocus(): void {
    this.view?.focus();
  }

  replace(text: string): void {
    const from = 0;
    const to = this.view?.state.doc.length;
    this.view?.dispatch({ changes: { from, to, insert: text } });

    setTimeout(() => {
      this.view?.focus();
    }, 0);
  }

  insert(text: string): void {
    if (!this.view) return;

    let from = 0;
    let to = 0;
    if (this.view.state.selection.ranges.length > 0) {
      const range = this.view.state.selection.ranges[0];
      from = range.from;
      to = range.to;
    }

    this.view.dispatch({
      changes: { from, to, insert: text },
      selection: { anchor: from + text.length },
    });

    this.view.focus();
  }

  async render(content: string): Promise<void> {
    try {
      const result = await this.mathService.renderEquation(content);
      const payload = {
        errors: result?.errors ?? [],
        node: result?.node ? this.sanitizer.bypassSecurityTrustHtml(result.node) : null,
      };
      this.lastResult = payload;
      this.results$.next(payload);
    } catch (e: any) {
      const payload = {
        errors: [{ id: 'Error', message: e.message.replace('MathJax parse error: ', '') }],
        node: null,
      };
      this.lastResult = payload;
      this.previewValidation$.next('');
      this.results$.next(payload);
    }
  }

  copyForAiHelp(): void {
    const tex = this.view?.state.doc.toString() ?? this.texInput$.value ?? '';
    const errors = this.lastResult?.errors ?? [];
    const isInline = this.isInline$.value;
    const errorLines = errors.map((e) => `- ${e.id}: ${e.message}`).join('\n');
    const text = `I have a LaTeX math equation that fails to render in MathJax 3.x (TeX/LaTeX input).

**Error(s):**
${errorLines}

**My LaTeX code:**
\`\`\`latex
${tex}
\`\`\`

**Equation type:** ${isInline ? 'inline' : 'block'} (${isInline ? 'rendered in text flow' : 'displayed on its own line'})

**Important notes:**
- MathJax 3.x uses strict TeX parsing and supports a subset of LaTeX (not all packages)
- For supported commands and environments, see the MathJax documentation
- Ensure brackets, braces, and parentheses are properly balanced

Please fix the LaTeX so it renders correctly in MathJax 3.x. Return only the corrected LaTeX code, no explanation.`;
    navigator.clipboard.writeText(text).then(
      () => this.snackBar.open('Copied to clipboard. Paste into your favorite AI assistant to get help.', undefined, { duration: 5000 }),
      () => this.snackBar.open('Could not copy to clipboard', undefined, { duration: 3000 }),
    );
  }

  acceptInlineToggle({ checked }): void {
    this.isInline$.next(checked);
  }

  acceptTexInput(tex: string): void {
    this.texInput$.next(tex);
  }

  apply(): void {
    this.dialogRef.close({
      numbered: !this.isInline$.value,
      tex: this.texInput$.value,
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
